import { useCallback, useEffect, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { logout, setToken } from '@/features/auth/authSlice.ts';
import { ssoConfig } from '@/features/login/constants';
import { refreshAuthToken } from '@/services/login/auth/authApi';

interface TokenPayload {
    exp?: number;
    role?: string[];
    sub?: string;
    preffered_username?: string;
    iat?: number;
    iss?: string;
    aud?: string;
    [key: string]: unknown;
}

/**
 * useTokenRefresh — Proactive JWT Token Refresh Hook
 *
 * @description
 * Silently renews the RWF SSO JWT **before** it expires, keeping the user's
 * session alive without forcing a re-login.
 *
 * ---
 * ### Token lifecycle
 * | Stage | Timing |
 * |---|---|
 * | Token issued | t = 0 |
 * | **Refresh triggered** | t = 60 min (5 min before expiry) |
 * | Token expires | t = 65 min (exp = iat + 3900 s) |
 *
 * The 5-minute buffer is read from `ssoConfig.tokenLifecycle.bufferTime`
 * (milliseconds) so it can be adjusted per environment without touching this
 * hook.
 *
 * ---
 * ### Behaviour
 * 1. **Scheduled refresh** — When a non-null `token` lands in the Redux store,
 *    the hook decodes the JWT, calculates `exp - bufferTime - now`, and sets a
 *    `setTimeout` for that delay.  If the token is already within the buffer
 *    window, the refresh fires immediately.
 * 2. **Window-focus refresh** — Fires `silentRefreshToken` when the user
 *    returns to the tab and the token is within the buffer window.  Guards
 *    against the case where the machine was suspended while a timer was pending.
 * 3. **Concurrent-refresh guard** — `isRefreshingRef` prevents duplicate
 *    in-flight requests even when the scheduled timer and a focus event fire at
 *    the same time.
 * 4. **Expiry check before refresh** — The token is re-decoded immediately
 *    before the API call; if it has already expired the user is logged out
 *    without a network round-trip.
 * 5. **Failure → logout** — Any network error during refresh dispatches
 *    `logout()`, clearing the store and returning the user to the login page.
 *
 * ---
 * ### Refresh endpoint
 * `POST /auth-service/api/sso/refresh`
 * Request body: `{ "token": "<currentToken>" }` — no Authorization header needed.
 * Success response: `{ status: "success", data: [{ token: string }], timestamp: string }`
 * The new token is extracted from `data[0].token`.
 *
 * ---
 * ### Usage
 * Call once inside a top-level authenticated layout or route guard component.
 * Do **not** call multiple times; a single instance manages the timer for the
 * entire session.
 *
 * ```tsx
 * // e.g. inside AuthenticatedLayout.tsx
 * useTokenRefresh();
 * ```
 *
 * @returns {object}
 * @property {() => Promise<void>} silentRefreshToken - Manually trigger a
 *   token refresh.  Useful for testing or force-refresh scenarios.
 * @property {(jwtToken: string) => void} scheduleRefresh - Re-schedule the
 *   refresh timer for a given JWT string.  Called automatically after each
 *   successful refresh; exposed for testing.
 * @property {(jwtToken: string) => TokenPayload | null} decodeToken - Decode
 *   a JWT string and return its payload, or `null` if the token is malformed.
 *   Exposed for testing.
 */
export function useTokenRefresh() {
    const dispatch = useAppDispatch();
    const token = useAppSelector((state) => state.authStore.token);
    const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isRefreshingRef = useRef(false);
    const silentRefreshTokenRef = useRef<(() => Promise<void>) | null>(null);

    const decodeToken = useCallback((jwtToken: string): TokenPayload | null => {
        try {
            return jwtDecode<TokenPayload>(jwtToken);
        } catch (error) {
            console.error('[TokenRefresh] Error decoding JWT:', error);
            return null;
        }
    }, []);

    const scheduleRefresh = useCallback(
        (jwtToken: string) => {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
            }

            const decoded = decodeToken(jwtToken);
            if (!decoded || !decoded.exp) {
                console.error('[TokenRefresh] Cannot schedule refresh - invalid token');
                return;
            }

            const expiresAt = decoded.exp * 1000;
            const now = Date.now();
            const bufferTime = ssoConfig.tokenLifecycle.bufferTime;
            const delay = expiresAt - now - bufferTime;

            if (delay <= 0) {
                console.warn('[TokenRefresh] Token expires in less than 5 minutes, refreshing now');
                silentRefreshTokenRef.current?.();
                return;
            }

            refreshTimerRef.current = setTimeout(() => {
                silentRefreshTokenRef.current?.();
            }, delay);
        },
        [decodeToken],
    );

    const silentRefreshToken = useCallback(async () => {
        if (isRefreshingRef.current) {
            return;
        }

        isRefreshingRef.current = true;

        try {
            const currentToken = token;
            if (!currentToken) {
                console.warn('[TokenRefresh] No token to refresh');
                isRefreshingRef.current = false;
                return;
            }

            // Verify token is still valid before attempting refresh
            const decoded = decodeToken(currentToken);
            if (!decoded || !decoded.exp) {
                console.error('[TokenRefresh] Invalid token structure');
                dispatch(logout());
                isRefreshingRef.current = false;
                return;
            }

            const now = Math.floor(Date.now() / 1000);
            if (decoded.exp <= now) {
                console.warn('[TokenRefresh] Token already expired, logging out');
                dispatch(logout());
                isRefreshingRef.current = false;
                return;
            }

            // POST body must contain the current token — the endpoint returns
            // 400 "Failed to read request" when the body is absent or empty.
            // Success shape: { status: "success", data: [{ token: string }] }
            const response = await refreshAuthToken(dispatch, { token: currentToken });

            // Update Redux store with new token
            dispatch(setToken(response.data[0].token));

            // Schedule next refresh
            scheduleRefresh(response.data[0].token);

            isRefreshingRef.current = false;
        } catch (error: unknown) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            console.error('[TokenRefresh] Refresh failed:', errorMsg);

            dispatch(logout());
            isRefreshingRef.current = false;
        }
    }, [token, decodeToken, dispatch, scheduleRefresh]);

    useEffect(() => {
        silentRefreshTokenRef.current = silentRefreshToken;
    }, [silentRefreshToken]);

    const handleWindowFocus = useCallback(() => {
        if (!token) return;

        const decoded = decodeToken(token);
        if (!decoded || !decoded.exp) return;

        const expiresAt = decoded.exp * 1000;
        const now = Date.now();
        const timeUntilExpiry = expiresAt - now;
        const bufferTime = ssoConfig.tokenLifecycle.bufferTime;

        if (timeUntilExpiry < bufferTime) {
            silentRefreshToken();
        }
    }, [token, decodeToken, silentRefreshToken]);

    useEffect(() => {
        if (token) {
            scheduleRefresh(token);
        } else if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }

        return () => {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
            }
        };
    }, [token, scheduleRefresh]);

    useEffect(() => {
        window.addEventListener('focus', handleWindowFocus);

        return () => {
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [handleWindowFocus]);

    return {
        silentRefreshToken,
        scheduleRefresh,
        decodeToken,
    };
}
